import httpService from "@/services/http.service";
import { onMounted, ref, onUnmounted } from "vue";
import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging";
import storageService from "@/services/storage.service";
import { StorageKeys } from "@/helpers/enum";
import { AppEnvironments, Environment } from "@/environment";
import { NotificationComposables } from "@/components/notifications-old/notification.composables";
import { endInteraction, startInteraction, trackError, trackEvent } from "@/helpers/tracking.helpers";
import { useNotificationStore } from "@/components/notifications-old/stores/notification-new.store";

const { getNotifications, unregisterSubscribe } = NotificationComposables();

let swRegistration: any;
export const useFCMNotification = () => {
  const deviceId = ref<string>(storageService.GetProperty(StorageKeys.deviceId));
  const fcmToken = ref<string>(storageService.GetProperty(StorageKeys.fcmToken));
  const isOnline = ref<boolean>(navigator.onLine);
  const notificationStore = useNotificationStore();
  
  // Online/offline detection
  const setupOnlineOfflineListeners = () => {
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
  };
  
  const removeOnlineOfflineListeners = () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
  
  const handleOnline = () => {
    isOnline.value = true;
    trackEvent('AppOnline');
    syncOfflineNotifications();
    processPendingRegistrations();
  };
  
  const handleOffline = () => {
    isOnline.value = false;
    trackEvent('AppOffline');
  };
  
  const syncOfflineNotifications = async (): Promise<void> => {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    
    // Check if Background Sync API is available
    const isBackgroundSyncAvailable = 'sync' in registration;
    
    if (isBackgroundSyncAvailable) {
      // Use type assertion to tell TypeScript about the sync property
      const syncManager = (registration as any).sync as SyncManager;
      
      if (syncManager) {
        try {
          await syncManager.register('sync-offline-notifications');
          trackEvent('OfflineSyncRegistered');
        } catch (syncError) {
          console.warn('Background Sync registration failed:', syncError);
          trackError(syncError as Error, { context: 'BackgroundSyncRegistration' });
          
          // Fallback to direct method if background sync fails
          await syncDirectlyWithSW();
        }
      }
    } else {
      // Background Sync API not available, use fallback
      await syncDirectlyWithSW();
    }
  } catch (error) {
    trackError(error as Error, { context: 'BackgroundSync' });
  }
};

const syncDirectlyWithSW = async (): Promise<void> => {
  try {
    const notifications = await getOfflineNotificationsFromSW();
    if (notifications && notifications.length > 0) {
      await processOfflineNotifications(notifications);
      trackEvent('OfflineNotificationsSyncedDirect', {
        count: notifications.length
      });
    }
  } catch (error) {
    trackError(error as Error, { context: 'DirectSyncWithSW' });
  }
};
  
  const getOfflineNotificationsFromSW = (): Promise<any[]> => {
    return new Promise((resolve) => {
      if (!navigator.serviceWorker.controller) {
        resolve([]);
        return;
      }
      
      const channel = new MessageChannel();
      channel.port1.onmessage = (event) => {
        if (event.data.success) {
          resolve(event.data.notifications || []);
        } else {
          resolve([]);
        }
      };
      
      navigator.serviceWorker.controller.postMessage({
        type: 'GET_OFFLINE_NOTIFICATIONS',
        userId: storageService.user.value?._id
      }, [channel.port2]);
    });
  };
  
  const processOfflineNotifications = async (notifications: any[]) => {
    for (const notification of notifications) {
      try {
        // Convert to your app's notification format
        const appNotification = transformToAppNotification(notification);
        notificationStore.handleNewNotification(appNotification);
        
        // Mark as read in service worker storage
        await markNotificationAsReadInSW(notification.messageId);
        
        trackEvent('OfflineNotificationProcessed', {
          messageId: notification.messageId
        });
      } catch (error) {
        trackError(error as Error, {
          context: 'ProcessOfflineNotification',
          messageId: notification.messageId
        });
      }
    }
  };
  
  const markNotificationAsReadInSW = (messageId: string): Promise<boolean> => {
    return new Promise((resolve) => {
      if (!navigator.serviceWorker.controller) {
        resolve(false);
        return;
      }
      
      const channel = new MessageChannel();
      channel.port1.onmessage = (event) => {
        resolve(event.data.success || false);
      };
      
      navigator.serviceWorker.controller.postMessage({
        type: 'MARK_NOTIFICATION_READ',
        messageId
      }, [channel.port2]);
    });
  };
  
  const transformToAppNotification = (notification: any): any => {
    // Transform the service worker notification to your app's format
    return {
      id: notification.messageId,
      notificationId: notification.messageId,
      notificationType: notification.data?.type || 'general',
      title: notification.notification?.title,
      body: notification.notification?.body,
      data: notification.data,
      isRead: notification.isRead || false,
      isViewed: true,
      createdAt: notification.receivedAt || new Date().toISOString(),
      isOffline: true
    };
  };

  const unregister = () => {
    unregisterSubscribe();
    removeOnlineOfflineListeners();
  };

  const registerServiceWorker = () => {
    if ("serviceWorker" in navigator) {
      const environment = getEnvironmentPath();
      
      return navigator.serviceWorker
        .register(`/${environment}-fcm-sw.js`)
        .then((registration) => {
          swRegistration = registration;
          trackEvent('ServiceWorkerRegistered', { environment });
          return true;
        })
        .catch((error) => {
          trackError(error as Error, { context: 'ServiceWorkerRegistration' });
          return false;
        });
    }
    return Promise.resolve(false);
  };
  
  const getEnvironmentPath = (): string => {
    if (Environment.currentEnvironment == AppEnvironments.production) {
      return "production";
    } else if (
      Environment.currentEnvironment == AppEnvironments.uat ||
      Environment.currentEnvironment == AppEnvironments.api6uat
    ) {
      return "uat";
    } else if (Environment.currentEnvironment == AppEnvironments.stage) {
      return "stage";
    } else if (Environment.currentEnvironment == AppEnvironments.dev) {
      return "dev";
    }
    return "internal";
  };

  const registerForNotification = (): Promise<boolean> => {
    return new Promise(async (resolve, reject) => {
      try {
        await getNotifications();
    console.log('FCM Device ID and Token setting...'
    );

        const messaging = getMessaging();
        const deviceIdValue = getDeviceId();
        const token = await getToken(messaging, {
          serviceWorkerRegistration: swRegistration,
        });

        trackEvent('FCMTokenReceived', {
          tokenLength: token?.length || 0,
          hasSW: !!swRegistration
        });
    console.log('FCM Device ID and Token set:', deviceIdValue, token);

        await setTokenAndId(deviceIdValue, token);
        resolve(true);
      } catch (e) {

            console.log('FCM Device ID and Token set:',e);

        trackError(e as Error, {
          context: 'FCMRegistration',
          hasSW: !!swRegistration
        });
        resolve(false);
      }
    });
  };

  const getDeviceId = (): string => {
    const userAgent = navigator.userAgent;
    const cookieEnabled = navigator.cookieEnabled;
    return `${userAgent}-${cookieEnabled}`;
  };

  const setTokenAndId = async (id: string, token: string) => {
    if (deviceId.value && fcmToken.value) {
      // Optionally unregister old device if needed
    }
    
    deviceId.value = id;
    fcmToken.value = token;
    
    // Store locally for offline access
    storageService.SetProperty(StorageKeys.deviceId, id);
    storageService.SetProperty(StorageKeys.fcmToken, token);
    
    if (isOnline.value) {
      await registerDevice();
    } else {
      // Queue registration for when online
      await queueOfflineRegistration();
    }
  };
  
  const queueOfflineRegistration = async (): Promise<void> => {
    const pendingRegistrations = JSON.parse(
      storageService.GetProperty(StorageKeys.pendingFCMRegistrations) || '[]'
    );
    
    pendingRegistrations.push({
      deviceId: deviceId.value,
      deviceToken: fcmToken.value,
      timestamp: Date.now()
    });
    
    storageService.SetProperty(
      StorageKeys.pendingFCMRegistrations,
      JSON.stringify(pendingRegistrations)
    );
    
    trackEvent('FCMRegistrationQueuedOffline');
  };
  
  const processPendingRegistrations = async (): Promise<void> => {
    const pendingRegistrations = JSON.parse(
      storageService.GetProperty(StorageKeys.pendingFCMRegistrations) || '[]'
    );
    
    for (const registration of pendingRegistrations) {
      try {
        await httpService.registerFcm({
          deviceId: registration.deviceId,
          deviceToken: registration.deviceToken,
          deviceType: "web",
          appVersion: "1.0.0",
          deviceInfo: {
            platform: "web",
            model: navigator.userAgent,
            osVersion: navigator.platform
          }
        });
        
        trackEvent('PendingFCMRegistrationProcessed');
      } catch (error) {
        trackError(error as Error, { context: 'PendingFCMRegistration' });
        // Keep in queue if still failing
        continue;
      }
    }
    
    // Clear processed registrations
    storageService.RemoveProperty(StorageKeys.pendingFCMRegistrations);
  };

  const registerDevice = async (): Promise<void> => {
    try {
      await httpService.registerFcm({
        deviceId: deviceId.value,
        deviceToken: fcmToken.value,
        deviceType: "web",
        appVersion: "1.0.0",
        deviceInfo: {
          platform: "web",
          model: navigator.userAgent,
          osVersion: navigator.platform
        }
      });
      
      listenMessage();
      trackEvent('FCMDeviceRegistered');
    } catch (e) {
      trackError(e as Error, { context: 'FCMDeviceRegistration' });
      
      // If registration fails due to network, queue for later
      if (!isOnline.value) {
        await queueOfflineRegistration();
      }
    }
  };

  const listenMessage = (): void => {
    const messaging = getMessaging();
    onMessage(messaging, (payload) => {
      console.log('Message received. ', payload);
      const messageId = payload.messageId;
      const notificationType = payload.notification?.title || 'data';
      const interaction = startInteraction('HandleNotification', {
        messageId: messageId,
        notificationType: notificationType
      });

      try {
        trackEvent('NotificationReceived', {
          messageId: messageId,
          notificationType: notificationType,
          priority: payload?.fcmOptions?.analyticsLabel || 'normal',
          isOnline: isOnline.value
        });

        // Handle the notification based on online status
        if (isOnline.value) {
          // Process immediately if online
          handleOnlineNotification(payload);
        } else {
          // Store for later processing if offline
          handleOfflineNotification(payload);
        }

      } catch (error) {
        trackError(error as Error, {
          context: 'OnMessageHandler',
          messageId: messageId
        });
      } finally {
        endInteraction(interaction);
      }
    });
  };
  
  const handleOnlineNotification = (payload: any): void => {
    // Convert to your app's notification format
    const notification = {
      id: payload.messageId,
      notificationId: payload.messageId,
      notificationType: payload.data?.type || 'general',
      title: payload.notification?.title,
      body: payload.notification?.body,
      data: payload.data,
      isRead: false,
      isViewed: false,
      createdAt: new Date().toISOString()
    };
    
    // Add to store
    notificationStore.handleNewNotification(notification as any);
    

  };
  
  const handleOfflineNotification = async (payload: any): Promise<void> => {
    // In offline mode, we rely on the service worker to store the notification
    // The service worker will handle displaying the browser notification
    trackEvent('NotificationReceivedOffline', {
      messageId: payload.messageId
    });
    
    // We can also store a local copy for when the app comes back online
    const offlineNotifications = JSON.parse(
      storageService.GetProperty(StorageKeys.offlineAppNotifications) || '[]'
    );
    
    offlineNotifications.push({
      ...payload,
      receivedAt: new Date().toISOString()
    });
    
    // Keep only recent notifications (last 50)
    const recentNotifications = offlineNotifications.slice(-50);
    
    storageService.SetProperty(
      StorageKeys.offlineAppNotifications,
      JSON.stringify(recentNotifications)
    );
  };

  // Initialize
  onMounted(() => {
    setupOnlineOfflineListeners();
    
    // Process any pending operations when component mounts
    if (isOnline.value) {
      syncOfflineNotifications();
      processPendingRegistrations();
    }
  });
  
  onUnmounted(() => {
    removeOnlineOfflineListeners();
  });

  return {
    deviceId,
    fcmToken,
    registerServiceWorker,
    registerForNotification,
    setTokenAndId,
    getDeviceId,
    unregister,
    isOnline,
    syncOfflineNotifications
  };
};